import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useRegion } from '../context/RegionContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
} from '../components/ui/dialog';
import { 
  Building2, 
  Shield, 
  Bell, 
  FileText, 
  Wrench, 
  Calendar, 
  CreditCard, 
  Users, 
  QrCode,
  ArrowRight,
  CheckCircle2,
  Play,
  X,
  MessageSquare,
  ShoppingBag,
  Vote,
  BarChart3,
  Smartphone,
  AlertTriangle,
  Mail,
  FileSpreadsheet,
  Clock,
  Briefcase,
  Star,
  ChevronRight,
  Loader2,
  Globe,
  Gift,
  Facebook,
  Youtube,
} from 'lucide-react';

const coreFeatures = [
  { 
    icon: Wrench, 
    title: 'Maintenance Management', 
    description: 'Residents can log maintenance issues instantly, upload photos, and track progress in real time.' 
  },
  { 
    icon: CreditCard, 
    title: 'Levy Billing & Financial Management', 
    description: 'Upload levy statements, track payments, and manage financial documents securely.' 
  },
  { 
    icon: BarChart3, 
    title: 'Managing Agent Dashboard', 
    description: 'Managing agents can manage multiple buildings from one centralized dashboard.' 
  },
  { 
    icon: Briefcase, 
    title: 'Contractor Marketplace', 
    description: 'Request quotes, assign contractors, and track repair progress.' 
  },
  { 
    icon: Shield, 
    title: 'Insurance Claim Tracking', 
    description: 'Submit and monitor insurance claims with full visibility and audit history.' 
  },
  { 
    icon: QrCode, 
    title: 'Security & Visitor Access', 
    description: 'Residents can register visitors and generate QR access passes for estates and complexes.' 
  },
  { 
    icon: ShoppingBag, 
    title: 'Resident Marketplace', 
    description: 'Residents can buy, sell, and share services within their community.' 
  },
  { 
    icon: Vote, 
    title: 'Digital Voting', 
    description: 'Run AGMs and trustee decisions online with secure digital voting.' 
  },
];

const problemPoints = [
  'WhatsApp groups for maintenance',
  'Endless email chains',
  'Spreadsheets for finances',
  'Paper visitor registers',
  'Missing documents and poor communication',
];

const agentFeatures = [
  'Multi-building management',
  'Centralized maintenance tracking',
  'Financial document uploads',
  'Automated trustee reports',
  'Compliance monitoring',
];

const residentFeatures = [
  'Log maintenance issues',
  'Receive notices and announcements',
  'Register visitors',
  'Access documents',
  'Pay levies',
  'Connect with their community',
];

const setupSteps = [
  { step: 1, title: 'Create your building profile', description: 'Set up your complex in minutes' },
  { step: 2, title: 'Upload units and residents', description: 'Import or add manually' },
  { step: 3, title: 'Start managing', description: 'Maintenance, communication, and finances' },
];

export default function Landing() {
  const [showDemo, setShowDemo] = useState(false);
  const { user, loading: authLoading } = useAuth();
  const { region, loading: regionLoading } = useRegion();
  const navigate = useNavigate();
  
  // Pricing based on region
  const pricing = region?.code === 'INTERNATIONAL' 
    ? { symbol: '$', price: 12, currency: 'USD', minPrice: 250, examples: { small: '$250', large: '$600' } }
    : { symbol: 'R', price: 35, currency: 'ZAR', minPrice: 750, examples: { small: 'R750', large: 'R1,750' } };

  // Note: Logged-in users still see the homepage. A "Go to Dashboard"
  // CTA in the header lets them jump back into the app. We intentionally
  // do NOT auto-redirect from "/" so that visiting bodycorp365.com always
  // shows the marketing homepage (fixes stale-session bounce-to-/login).
  const goToDashboard = () => {
    if (!user) return;
    if (user.role === 'platform_owner') navigate('/platform');
    else if (['managing_agent', 'agent_admin', 'super_agent', 'general_agent'].includes(user.role)) navigate('/agent');
    else if (['property_manager', 'trustee', 'chairman'].includes(user.role)) navigate('/admin');
    else navigate('/dashboard');
  };

  // Show loading while checking auth
  // We intentionally render the homepage immediately. Auth state is only
  // used to decide which CTA to show in the nav (Sign In vs Go to Dashboard).

  return (
    <div className="min-h-screen bg-white">
      {/* Video Demo Modal */}
      <Dialog open={showDemo} onOpenChange={setShowDemo}>
        <DialogContent className="max-w-4xl p-0 bg-black border-0 overflow-hidden">
          <button 
            onClick={() => setShowDemo(false)}
            className="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <video 
            autoPlay 
            controls 
            className="w-full aspect-video"
            data-testid="demo-video"
          >
            <source src="/demo-video.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </DialogContent>
      </Dialog>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <img 
                src="/logo.png" 
                alt="BodyCorp365" 
                className="w-10 h-10"
              />
              <span className="text-lg font-bold font-manrope text-slate-900">BodyCorp365</span>
            </div>
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-slate-600 hover:text-slate-900 transition-colors">Features</a>
              <Link to="/pricing-info" className="text-slate-600 hover:text-slate-900 transition-colors">Pricing</Link>
              <Link to="/about" className="text-slate-600 hover:text-slate-900 transition-colors">How It Works</Link>
              <a href="#agents" className="text-slate-600 hover:text-slate-900 transition-colors">For Agents</a>
              <a href="mailto:hello@bodycorp365.com" className="text-slate-600 hover:text-slate-900 transition-colors">Contact</a>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/agent-signup" className="hidden md:inline-block" data-testid="nav-agent-signup-link">
                <Button variant="ghost" className="text-amber-700 hover:text-amber-800 hover:bg-amber-50 font-medium" data-testid="nav-agent-signup-btn">
                  <Gift className="mr-1.5 w-4 h-4" /> Agents — Sign up free
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="ghost" className="hidden sm:flex" data-testid="nav-login-btn">
                  Sign In
                </Button>
              </Link>
              <Link to="/signup">
                <Button className="bg-gradient-to-r from-blue-600 to-emerald-500 hover:from-blue-700 hover:to-emerald-600 rounded-full px-6" data-testid="nav-get-started-btn">
                  Buy Now
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-24 px-4 overflow-hidden bg-gradient-to-br from-blue-900 via-blue-800 to-slate-900">
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=1920)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        ></div>
        <div className="relative max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-manrope text-white mb-6 leading-tight">
                Smart Management for{' '}
                <span className="text-emerald-300">Residential Communities</span>
              </h1>
              <p className="text-lg sm:text-xl text-blue-100 mb-3 leading-relaxed">
                BodyCorp365 connects residents, trustees, and managing agents in one powerful platform 
                for maintenance, finances, communication, and security.
              </p>
              <Link
                to="/agent-signup"
                className="inline-flex items-center gap-1.5 text-amber-300 hover:text-amber-200 text-sm font-medium underline underline-offset-4 mb-6"
                data-testid="hero-agent-signup-link"
              >
                <Gift className="w-4 h-4" /> Are you a managing agent? Sign up here for free — earn 10% commission →
              </Link>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/free-trial">
                  <Button 
                    size="lg" 
                    className="bg-white text-blue-900 hover:bg-blue-50 rounded-full px-8 h-14 text-lg font-semibold shadow-xl"
                    data-testid="hero-trial-btn"
                  >
                    <Gift className="mr-2 w-5 h-5" />
                    Start Your 1 Month Free Trial
                  </Button>
                </Link>
              </div>
            </div>
            <div className="hidden lg:block">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/0e0e64bc-9297-498d-81ba-2e62fc7389a5/images/aad6252c5113638fc1a12e00dc402aa30ddfb02e855dbce0b32eb723349db1d8.png"
                alt="BodyCorp365 dashboard on laptop and smartphone showing building overview, trustee announcements, and service providers"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* The Problem Section */}
      <section className="py-20 px-4 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-red-100 text-red-700 rounded-full px-4 py-2 mb-6">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-sm font-medium">The Challenge</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold font-manrope text-slate-900 mb-6">
                Managing a Complex Shouldn't Be This Difficult
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Most residential complexes still rely on scattered systems:
              </p>
              <ul className="space-y-4">
                {problemPoints.map((point, index) => (
                  <li key={index} className="flex items-center gap-3 text-slate-700">
                    <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                      <X className="w-4 h-4 text-red-600" />
                    </div>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-8 text-lg text-slate-600 italic border-l-4 border-red-300 pl-4">
                This leads to frustration for residents, trustees, and managing agents.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200">
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
                    <MessageSquare className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Scattered Communication</h4>
                    <p className="text-slate-500 text-sm">Multiple WhatsApp groups, lost emails</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <FileSpreadsheet className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Manual Tracking</h4>
                    <p className="text-slate-500 text-sm">Spreadsheets and paper-based systems</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Delayed Responses</h4>
                    <p className="text-slate-500 text-sm">Slow issue resolution and follow-ups</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Solution Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-blue-600 to-emerald-500 text-white">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold font-manrope mb-6">
            One Platform. Complete Control.
          </h2>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto mb-4">
            BodyCorp365 simplifies residential community management by bringing everything together in one digital platform.
          </p>
          <p className="text-lg text-blue-100 max-w-2xl mx-auto mb-8">
            Residents, trustees, managing agents, contractors, and security teams can collaborate seamlessly.
          </p>
          <div className="flex flex-wrap justify-center gap-4 text-blue-100">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-400" />
              <span>No more lost emails</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-400" />
              <span>No more manual systems</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-400" />
              <span>Just efficient property management</span>
            </div>
          </div>
        </div>
      </section>

      {/* Core Features Section */}
      <section id="features" className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold font-manrope text-slate-900 mb-4">
              Core Features
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Everything you need to manage your residential community efficiently
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {coreFeatures.map((feature, index) => (
              <Card 
                key={feature.title}
                className="group bg-white hover:shadow-xl hover:border-blue-200 transition-all duration-300 border-slate-200"
              >
                <CardContent className="p-6">
                  <div className="w-14 h-14 rounded-xl bg-blue-100 flex items-center justify-center mb-4 group-hover:bg-gradient-to-br group-hover:from-blue-600 group-hover:to-emerald-500 group-hover:scale-110 transition-all duration-300">
                    <feature.icon className="w-7 h-7 text-blue-600 group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-lg font-semibold font-manrope text-slate-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Built for Managing Agents */}
      <section id="agents" className="py-20 px-4 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-blue-500/20 text-blue-300 rounded-full px-4 py-2 mb-6">
                <BarChart3 className="w-4 h-4" />
                <span className="text-sm font-medium">For Managing Agents</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold font-manrope mb-6">
                Manage Multiple Buildings From One Platform
              </h2>
              <p className="text-lg text-slate-300 mb-8">
                BodyCorp365 allows managing agents to oversee all their complexes from a single dashboard. 
                Save time while delivering a more professional service.
              </p>
              <ul className="space-y-4 mb-8">
                {agentFeatures.map((feature, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                    <span className="text-slate-200">{feature}</span>
                  </li>
                ))}
              </ul>
              <Link to="/agent-request-access" data-testid="agents-signup-cta">
                <Button 
                  size="lg" 
                  className="bg-gradient-to-r from-blue-500 to-emerald-500 hover:from-blue-600 hover:to-emerald-600 rounded-full px-8"
                  data-testid="agents-signup-btn"
                >
                  Agents Sign up for FREE
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=600"
                alt="Managing agent using dashboard"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-white rounded-xl p-4 shadow-lg">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Multi-Building</p>
                    <p className="text-sm text-slate-500">Manage 10+ properties</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Resident Experience */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="bg-slate-900 rounded-3xl p-8 relative">
                <div className="absolute -top-4 -left-4 w-16 h-16 bg-gradient-to-br from-blue-600 to-emerald-500 rounded-2xl flex items-center justify-center shadow-lg">
                  <Smartphone className="w-8 h-8 text-white" />
                </div>
                <div className="ml-8 space-y-4">
                  {residentFeatures.slice(0, 4).map((feature, index) => (
                    <div key={index} className="flex items-center gap-3 bg-white/10 rounded-lg p-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                      <span className="text-white">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 bg-sky-100 text-sky-700 rounded-full px-4 py-2 mb-6">
                <Smartphone className="w-4 h-4" />
                <span className="text-sm font-medium">Mobile App</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold font-manrope text-slate-900 mb-6">
                A Better Experience for Residents
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Residents can use the BodyCorp365 mobile app to stay connected with their community:
              </p>
              <ul className="space-y-3 mb-8">
                {residentFeatures.map((feature, index) => (
                  <li key={index} className="flex items-center gap-3 text-slate-700">
                    <ChevronRight className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <div className="flex items-center gap-3 p-4 bg-emerald-50 rounded-xl border border-emerald-200">
                <Bell className="w-6 h-6 text-emerald-600" />
                <span className="text-emerald-800">Residents also receive notifications via <strong>push notifications</strong></span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Simple Setup */}
      <section className="py-20 px-4 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold font-manrope text-slate-900 mb-4">
              Get Started in Minutes
            </h2>
            <p className="text-lg text-slate-600">Simple 3-step setup process</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {setupSteps.map((item, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-2xl p-8 text-center shadow-sm border border-slate-200 hover:shadow-lg transition-shadow">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-600 to-emerald-500 text-white text-2xl font-bold flex items-center justify-center mx-auto mb-6">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-semibold font-manrope text-slate-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-slate-600">{item.description}</p>
                </div>
                {index < setupSteps.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ArrowRight className="w-8 h-8 text-blue-300" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold font-manrope text-slate-900 mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-lg text-slate-600">One affordable price for your entire building</p>
            {region && (
              <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-slate-100 rounded-full text-sm text-slate-600">
                <Globe className="w-4 h-4" />
                <span>{region.flag} Pricing in {region.currency}</span>
              </div>
            )}
          </div>
          <Card className="border-blue-600 border-2 relative overflow-hidden shadow-xl max-w-xl mx-auto">
            <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-blue-600 to-emerald-500 text-white text-center py-2 text-sm font-medium">
              Complete Platform Access
            </div>
            <CardContent className="p-8 pt-14">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-semibold text-slate-900 mb-4">BodyCorp365 Full Access</h3>
                <div className="mb-2">
                  <span className="text-5xl font-bold text-slate-900">{pricing.symbol}{pricing.price}</span>
                  <span className="text-slate-500 text-lg"> per unit / month</span>
                </div>
                <p className="text-slate-500">Billed annually (12 months)</p>
                <p className="text-blue-600 font-medium mt-2">Minimum {pricing.symbol}{pricing.minPrice}/month</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4 mb-6 space-y-2">
                <p className="text-center text-slate-700">
                  <span className="font-semibold">Small complex (10 units):</span> <span className="text-blue-600 font-bold">{pricing.examples.small}/month</span> <span className="text-slate-500">(min applies)</span>
                </p>
                <p className="text-center text-slate-700">
                  <span className="font-semibold">Large complex (50 units):</span> <span className="text-emerald-600 font-bold">{pricing.examples.large}/month</span>
                </p>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-2 text-slate-600">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                  Maintenance tracking & management
                </li>
                <li className="flex items-center gap-2 text-slate-600">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                  Levy billing & financial documents
                </li>
                <li className="flex items-center gap-2 text-slate-600">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                  Contractor marketplace
                </li>
                <li className="flex items-center gap-2 text-slate-600">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                  Insurance claim tracking
                </li>
                <li className="flex items-center gap-2 text-slate-600">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                  Digital voting for AGMs
                </li>
                <li className="flex items-center gap-2 text-slate-600">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                  Visitor access & QR codes
                </li>
                <li className="flex items-center gap-2 text-slate-600">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                  Resident mobile app access
                </li>
                <li className="flex items-center gap-2 text-slate-600">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                  Push notifications & announcements
                </li>
              </ul>
              <Link to="/signup">
                <Button className="w-full rounded-full bg-gradient-to-r from-blue-600 to-emerald-500 hover:from-blue-700 hover:to-emerald-600 h-12 text-lg font-semibold">
                  Get Started Today
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <p className="text-center text-slate-500 text-sm mt-4">
                No hidden fees • Cancel anytime • 14-day free trial
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold font-manrope text-slate-900 mb-4">
              What Our Users Say
            </h2>
          </div>
          <Card className="bg-white border-slate-200">
            <CardContent className="p-8">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-amber-400 fill-amber-400" />
                ))}
              </div>
              <blockquote className="text-xl text-slate-700 italic mb-6">
                "BodyCorp365 transformed communication in our complex. Maintenance issues are resolved 
                faster and residents stay informed. It's exactly what we needed."
              </blockquote>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">Board of Trustees</p>
                  <p className="text-slate-500">Residential Complex, Cape Town</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4 bg-gradient-to-r from-blue-600 to-emerald-500">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold font-manrope text-white mb-4">
            Modernize Your Complex Management
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join hundreds of communities already using BodyCorp365
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/free-trial">
              <Button 
                size="lg" 
                className="bg-white text-blue-900 hover:bg-blue-50 rounded-full px-8 h-14 text-lg font-semibold"
                data-testid="final-cta-btn"
              >
                <Gift className="mr-2 w-5 h-5" />
                Start Your 1 Month Free Trial
              </Button>
            </Link>
            <Link to="/login">
              <Button 
                size="lg"
                variant="outline"
                className="rounded-full px-8 h-14 text-lg border-white/30 text-white hover:bg-white/10"
              >
                Login
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="py-12 px-4 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img 
                  src="/logo.png" 
                  alt="BodyCorp365" 
                  className="w-10 h-10"
                />
                <span className="text-lg font-bold font-manrope">BodyCorp365</span>
              </div>
              <p className="text-slate-400 text-sm">
                Smart management for residential communities.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#agents" className="hover:text-white transition-colors">For Agents</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><Link to="/about" className="hover:text-white transition-colors">How It Works</Link></li>
                <li><a href="mailto:hello@bodycorp365.com" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-slate-400 text-sm">
              © 2026 BodyCorp365. All rights reserved.
            </p>
            <div className="flex items-center gap-4">
              <a
                href="https://www.facebook.com/share/1PmLRv1sJS/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="BodyCorp365 on Facebook"
                className="text-slate-400 hover:text-white transition-colors"
                data-testid="footer-facebook-link"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://www.youtube.com/@BodyCorp365"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="BodyCorp365 on YouTube"
                className="text-slate-400 hover:text-white transition-colors"
                data-testid="footer-youtube-link"
              >
                <Youtube className="w-5 h-5" />
              </a>
              <a href="mailto:hello@bodycorp365.com" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
                <Mail className="w-5 h-5" />
                <span className="text-sm">hello@bodycorp365.com</span>
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Floating Social Icons — bottom-left */}
      <div
        className="fixed bottom-6 left-6 z-50 flex flex-col gap-3"
        data-testid="floating-socials"
      >
        <a
          href="https://www.facebook.com/share/1PmLRv1sJS/"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="BodyCorp365 on Facebook"
          className="w-12 h-12 rounded-full bg-[#1877F2] text-white shadow-xl hover:scale-110 transition-transform flex items-center justify-center"
          data-testid="floating-facebook"
        >
          <Facebook className="w-6 h-6" />
        </a>
        <a
          href="https://www.youtube.com/@BodyCorp365"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="BodyCorp365 on YouTube"
          className="w-12 h-12 rounded-full bg-[#FF0000] text-white shadow-xl hover:scale-110 transition-transform flex items-center justify-center"
          data-testid="floating-youtube"
        >
          <Youtube className="w-6 h-6" />
        </a>
      </div>
    </div>
  );
}
